import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Calendar from '../../components/calendar'
import Card from '../../components/card'
import CardStatistics from '../../components/CardStatistics'
import GeneralDashboard from '../../components/dashboard/general'
import TableList from '../../components/tableList'
import { NavBarCollapsableContext } from '../../helpers/context'
import { useWindowSize } from '../../helpers/hooks/hookWindowsSize'
import { linkImage, qrImage } from '../../images'
import {
  formatDateLong,
  formatDateToReport,
  getToken,
  getTokenExpiration,
  logOut,
} from '../../helpers/utils'
import {
  getLinks,
  getTotalTracking,
  getTotalLinks,
} from '../../redux/reducers/urlReducer'
import { AppDispatch, useAppDispatch } from '../../redux/store'
import { useNavigate } from 'react-router-dom'
import { getInfo } from '../../redux/reducers/userReducer'

interface ImageSize {
  height: number
  width: number
}

interface CardOption {
  title: string
  image: string
  buttonTitle: string
  color: string
  url: string
  imageSize: ImageSize
}

const Dashboard = ({}: CardOption) => {
  let navigate = useNavigate()
  const { t } = useTranslation()
  const token = getToken()
  const classContext = useContext(NavBarCollapsableContext)
  const sizeWidth = useWindowSize().width
  const dispatch: AppDispatch = useAppDispatch()
  const [updateReports, setUpdateReports] = useState(false)
  const [linkData, setLinkData] = useState([])
  const [qrCodeData, setQrCodeData] = useState([])
  const [totalLinks, setTotalLinks] = useState<number>(0)
  const [totalQrCodes, setTotalQrCodes] = useState<number>(0)
  const [dataTotal, setDataTotal] = useState({
    links: 0,
    qrCodes: 0,
  })
  const [dataTotalPercentage, setDataTotalPercentage] = useState({
    links: 0,
    qrCodes: 0,
  })

  const [dataTotalTracking, setDataTotalTracking] = useState(0)
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  })
  const formattedStartDate = dateRange.startDate
    ? formatDateToReport(String(dateRange.startDate))
    : undefined
  const formattedEndDate = dateRange.endDate
    ? formatDateToReport(String(dateRange.endDate))
    : undefined
  const titleRangeDate =
    formattedStartDate && formattedEndDate
      ? `(${formatDateLong(String(dateRange.startDate))} - ${formatDateLong(
          String(dateRange.endDate)
        )})`
      : ''
  const sessionExpired = getTokenExpiration()

  useEffect(() => {
    if (sessionExpired) logOut()
  }, [sessionExpired])

  useEffect(() => {
    setDataTotal({
      links: totalLinks,
      qrCodes: totalQrCodes,
    })
  }, [totalLinks, totalQrCodes])

  useEffect(() => {
    getData()
    getDataLinks()
    getDataQRCodes()
    setUpdateReports(false)
    getTotalDataLinks()
    getTotalDataQrCodes()
  }, [updateReports])

  useEffect(() => {
    getUserInfo()
  }, [])

  const getUserInfo = async () => {
    try {
      const { payload } = await dispatch(
        getInfo({
          token: token,
        })
      )
      if (payload?.success) {
        if (payload.data.credits.url === 0) navigate('/plans')
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleSearchClick = () => {
    setUpdateReports(true)
  }

  const getDataLinks = async () => {
    try {
      const { payload } = await dispatch(
        getLinks({
          token: token,
          page: 1,
          hasqr: 0,
          status: 1,
          fromDate: '',
          toDate: '',
          tag: [],
        })
      )

      if (payload?.success) {
        setLinkData(payload?.data.slice(0, 4))
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getDataQRCodes = async () => {
    try {
      const { payload } = await dispatch(
        getLinks({
          token: token,
          page: 1,
          hasqr: 1,
          status: 1,
          fromDate: '',
          toDate: '',
          tag: [],
        })
      )
      if (payload?.success) {
        setQrCodeData(payload?.data.slice(0, 4))
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const cardsOptions = [
    {
      title: 'LINKS',
      image: linkImage,
      buttonTitle: 'GO_TO_LINKS',
      color: 'secondary',
      url: '/links',
      imageSize: { height: 75, width: 100 },
    },
    {
      title: 'QR_CODES',
      image: qrImage,
      buttonTitle: 'GO_TO_QR_CODES',
      color: 'red',
      url: '/qr-codes',
      imageSize: { height: 75, width: 100 },
    },
  ]

  const chartData = {
    labels: [`${t('LINKS')}`, `${t('QR_CODES')}`],
    series: [dataTotalPercentage.links, dataTotalPercentage.qrCodes],
  }

  const onChange = (ranges: any) => {
    setDateRange(ranges)
  }

  const getData = async () => {
    try {
      const { payload } = await dispatch(
        getTotalTracking({
          token: token,
          fromDate: formattedStartDate,
          toDate: formattedEndDate,
        })
      )
      const unAuth = payload?.errorMessage?.response?.status === 401

      setDataTotalPercentage({
        links: payload?.clicks?.total | 0,
        qrCodes: payload?.qr?.total | 0,
      })
      setDataTotalTracking(payload?.clicks?.total + payload?.qr?.total)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getTotalDataLinks = async () => {
    try {
      const { payload } = await dispatch(
        getTotalLinks({
          token: token,
          hasqr: 0,
          status: -99,
          fromDate: formattedStartDate,
          toDate: formattedEndDate,
          tag: [],
        })
      )

      if (payload?.success) {
        setTotalLinks(payload?.data?.total)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getTotalDataQrCodes = async () => {
    try {
      const { payload } = await dispatch(
        getTotalLinks({
          token: token,
          hasqr: 1,
          status: -99,
          fromDate: formattedStartDate,
          toDate: formattedEndDate,
          tag: [],
        })
      )

      if (payload?.success) {
        setTotalQrCodes(payload?.data?.total)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  return (
    <>
      <div className={`container-home`}>
        <div className={classContext.classes.navbar_padding_left}>
          <span className='principal-title'>{`${t('YOUR_CREATIONS')}`}</span>

          {sizeWidth > 767 ? (
            <div className='container-dashboard'>
              <div className='column-dashboard'>
                <div className='sub-column-dashboard'>
                  <div className='d-flex'>
                    {cardsOptions.map((option: CardOption) => {
                      return (
                        <Card
                          title={option.title}
                          image={option.image}
                          buttonTitle={option.buttonTitle}
                          color={option.color}
                          url={option.url}
                          imageSize={option.imageSize}
                        />
                      )
                    })}
                  </div>
                </div>

                <div className='plan-card m-2'>
                  <TableList dataBody={linkData} url={'/links/'} />
                </div>

                <div className='plan-card m-2'>
                  <TableList
                    dataBody={qrCodeData}
                    isQrList={true}
                    url={'/qr-codes/'}
                  />
                </div>
              </div>
              <div className='column-dashboard'>
                <div className='plan-card m-2'>
                  <span className='sub-title'>{`${t('FILTER_BY_DATE')}`}</span>
                  <Calendar onChange={onChange} />
                  <button
                    className='button-fifth'
                    type='submit'
                    onClick={() => {
                      setUpdateReports(true)
                    }}
                  >
                    {`${t('SEARCH')}`}
                  </button>
                </div>
                <div className='plan-card m-2 d-flex'>
                  <GeneralDashboard data={chartData} />
                </div>
                <div className='plan-card m-2 d-flex'>
                  <CardStatistics
                    data={dataTotal}
                    total={dataTotalTracking}
                    dateRange={titleRangeDate}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className='column-dashboard'>
                <div className='d-grid'>
                  {cardsOptions.map((option: CardOption) => {
                    return (
                      <Card
                        title={option.title}
                        image={option.image}
                        buttonTitle={option.buttonTitle}
                        color={option.color}
                        url={option.url}
                        imageSize={option.imageSize}
                      />
                    )
                  })}
                </div>

                <div className='overflow-auto'>
                  <div className='plan-card my-2 d-inline-block'>
                    <span className='sub-title'>{`${t(
                      'FILTER_BY_DATE'
                    )}`}</span>
                    <Calendar onChange={onChange} />
                    <button
                      className='button-fifth'
                      type='button'
                      onClick={handleSearchClick}
                    >
                      {t('SEARCH')}
                    </button>
                  </div>
                </div>
                <div className='plan-card my-2'>
                  <GeneralDashboard data={chartData} />
                </div>

                <div className='plan-card my-4 d-inline-block'>
                  <CardStatistics
                    data={dataTotal}
                    total={dataTotalTracking}
                    dateRange={`(${formattedStartDate} - ${formattedEndDate})`}
                  />
                </div>

                <div className='plan-card my-2'>
                  <TableList dataBody={linkData} url={'/links/'} />
                </div>

                <div className='plan-card my-4'>
                  <TableList
                    dataBody={qrCodeData}
                    isQrList={true}
                    url={'/qr-codes/'}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Dashboard
